import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { ApiError, type CatalogDef, type GmOnline, type ItemCatalog, type Pack, type PackItem } from '../api/types';
import { t } from '../i18n/idioma';
import { nomeDaLinhaGm, nomeDoGrupoDeItem, nomeDoItem, visualGm } from '../i18n/nomes';
import { ItemIcon } from './ItemIcon';
import { itemHint } from './itemUi';
import { prefetchIcons } from '../assets/items';
import { Abas, Linha } from './kit';

interface Props {
  active: boolean;
  tick: number;
  destinos?: readonly GmOnline[];
  /** Menu do GM › ficha › Dar item: já abre com esse jogador escolhido. */
  paraInicial?: string;
  /** Menu do GM › paleta (`item <nome>`): já abre com essa busca. */
  buscaInicial?: { readonly texto: string };
  onChanged(text: string): void;
}

type Tab = 'buy' | 'sell';

/** Para a busca: sem acento, minúsculo. */
const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
/** A busca pelo nome começa com 2 letras e mostra até 60 itens de todos os grupos. */
const MIN_BUSCA_ITEM = 2;
const LIMITE_BUSCA_ITEM = 60;

export function MarketGump({ active, tick, destinos, paraInicial, buscaInicial, onChanged }: Props) {
  const [tab, setTab] = useState<Tab>('buy');
  const [catalog, setCatalog] = useState<ItemCatalog | null>(null);
  const [pack, setPack] = useState<Pack | null>(null);
  const [group, setGroup] = useState<string | null>(null);
  const [qty, setQty] = useState<Record<string, number>>({});
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [para, setPara] = useState<string>('');
  useEffect(() => { setPara(paraInicial ?? ''); }, [paraInicial]);
  const [busca, setBusca] = useState('');
  useEffect(() => { if (buscaInicial) { setBusca(buscaInicial.texto); setTab('buy'); } }, [buscaInicial]);
  const nq = norm(busca);
  const buscando = nq.length >= MIN_BUSCA_ITEM;


  useEffect(() => {
    if (!active) return;
    let live = true;
    void api.itemCatalog()
      .then(cat => {
        if (!live) return;
        setCatalog(cat);
        setGroup(cur => cur ?? cat.groups[0]?.id ?? null);
        setError(null);
      })
      .catch(err => {
        if (live) setError(err instanceof ApiError ? err.message : t('gm.item.naoAbriu'));
      });
    void api.getPack()
      .then(nextPack => { if (live) setPack(nextPack); })
      .catch(err => {
        if (live) setError(err instanceof ApiError ? err.message : t('gm.item.naoAbriu'));
      });
    return () => { live = false; };
  }, [active, tick]);

  useEffect(() => {
    if (!catalog) return;
    const g = catalog.groups.find(x => x.id === group) ?? catalog.groups[0];
    if (g) prefetchIcons(g.items.map(i => visualGm(i.id).icone));
  }, [catalog, group]);

  /** Todos os itens com o nome na língua do jogo, para a busca. */
  const indice = useMemo(() => (catalog?.groups ?? []).flatMap(g => g.items.map(def => {
    const nome = nomeDaLinhaGm(def.id, def.name);
    return { def, grupo: g.id, chave: `${norm(nome)} ${norm(def.name)} ${def.id.toLowerCase()}`, nome: norm(nome) };
  })), [catalog]);

  const achados = useMemo(() => {
    if (!buscando) return null;
    const palavras = nq.split(/\s+/);
    return indice
      .filter(i => palavras.every(p => i.chave.includes(p)))
      .sort((a, b) => Number(!a.nome.startsWith(nq)) - Number(!b.nome.startsWith(nq)) || a.nome.localeCompare(b.nome));
  }, [indice, nq, buscando]);

  const items = useMemo(() => {
    if (achados) return achados.slice(0, LIMITE_BUSCA_ITEM).map(i => i.def);
    if (!catalog) return [];
    const g = catalog.groups.find(x => x.id === group) ?? catalog.groups[0];
    return g?.items ?? [];
  }, [catalog, group, achados]);
  const grupoDe = useMemo(() => new Map(indice.map(i => [i.def.id, i.grupo])), [indice]);

  useEffect(() => {
    if (achados?.length) prefetchIcons(achados.slice(0, LIMITE_BUSCA_ITEM).map(i => visualGm(i.def.id).icone));
  }, [achados]);

  const naMochila = (pack?.items.filter(i => !i.equippedSlot) ?? [])
    .filter(i => !buscando || `${norm(nomeDoItem(i.defId, i.name))} ${i.defId.toLowerCase()}`.includes(nq));

  const buy = async (def: CatalogDef) => {
    setBusy(true);
    setError(null);
    try {
      const amount = def.stackable ? clamp(qty[def.id] ?? 1, 1, 100) : 1;
      await api.buyItem(def.id, amount, para || undefined);
      onChanged(t('gm.item.vocePega', { item: nomeDaLinhaGm(def.id, def.name) }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('gm.item.naoPegou'));
    } finally {
      setBusy(false);
    }
  };

  const sell = async (item: PackItem) => {
    if (confirmId !== item.id) {
      setConfirmId(item.id);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const amount = item.qty > 1
        ? clamp(qty[item.id] ?? item.qty, 1, item.qty)
        : 1;
      await api.sellItem(item.id, amount);
      setConfirmId(null);
      onChanged(t('gm.item.voceVende', { item: nomeDoItem(item.defId, item.name) }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('gm.item.naoVendeu'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Abas
        forma="pilula"
        aria-label={t('gm.item.painel')}
        valor={tab}
        onMuda={id => setTab(id as Tab)}
        itens={[
          { id: 'buy', rotulo: t('gm.item.comprar') },
          { id: 'sell', rotulo: t('gm.item.vender') }
        ]}
      />

      {error && <p className="notice" role="alert">{error}</p>}

      <div className="mkt-busca">
        <input
          type="search"
          value={busca}
          maxLength={40}
          onChange={e => setBusca(e.target.value)}
          onKeyDown={e => { if (e.key === 'Escape' && busca) { e.stopPropagation(); setBusca(''); } }}
          placeholder={t(tab === 'buy' ? 'gm.item.buscar' : 'gm.item.buscarMochila')}
          aria-label={t(tab === 'buy' ? 'gm.item.buscar' : 'gm.item.buscarMochila')}
        />
        {buscando && tab === 'buy' && achados && (
          <small>
            {achados.length > LIMITE_BUSCA_ITEM
              ? t('gm.item.achadosDe', { n: LIMITE_BUSCA_ITEM, total: achados.length })
              : t('gm.item.achados', { n: achados.length })}
          </small>
        )}
      </div>

      {tab === 'buy' && destinos && destinos.length > 0 && (
        <label className="mkt-para">
          {t('gm.item.paraQuem')}
          <select value={para} onChange={e => setPara(e.target.value)}>
            <option value="">{t('gm.item.voce')}</option>
            {destinos.map(d => (
              <option key={d.id} value={d.id}>{d.name}{d.you ? t('gm.sufixoVoce') : ''}</option>
            ))}
          </select>
        </label>
      )}

      {tab === 'buy' && (
        <>
          {!buscando && <Abas
            forma="pilula"
            aria-label={t('gm.item.grupos')}
            valor={group ?? ''}
            onMuda={id => setGroup(id || null)}
            itens={(catalog?.groups ?? []).map(g => ({ id: g.id, rotulo: nomeDoGrupoDeItem(g.id) }))}
          />}
          <ul className="pack-list market-list">
            {!catalog && <li className="empty">{t('comum.montando')}</li>}
            {catalog && buscando && items.length === 0 && <li className="empty">{t('gm.item.nadaComEsseNome')}</li>}
            {items.map(def => {
              const vis = visualGm(def.id);
              const nome = nomeDaLinhaGm(def.id, def.name);
              return (
              <li key={def.id}>
                <Linha
                  lead={<ItemIcon id={vis.icone} label={nome} size={36} eager />}
                  trail={(
                    <>
                      <span className="pack-st">{t('gm.item.moedas', { n: 0 })}</span>
                      {def.stackable && (
                        <input
                          className="market-qty"
                          type="number"
                          min={1}
                          max={100}
                          value={qty[def.id] ?? 1}
                          onChange={e => setQty(cur => ({ ...cur, [def.id]: Number(e.target.value) }))}
                          aria-label={t('gm.item.quantidadeDe', { item: nome })}
                          disabled={busy}
                        />
                      )}
                      <button type="button" className="btn btn--tiny" disabled={busy} onClick={() => void buy(def)}>
                        {t('gm.item.pegar')}
                      </button>
                    </>
                  )}
                >
                  {nome}
                  {buscando && <small className="mkt-grupo">{nomeDoGrupoDeItem(grupoDe.get(def.id) ?? '')}</small>}
                </Linha>
              </li>
              );
            })}
          </ul>
        </>
      )}

      {tab === 'sell' && (
        <ul className="pack-list market-list">
          {pack && naMochila.length === 0 && (
            <li className="empty">{t(buscando ? 'gm.item.nadaComEsseNome' : 'gm.item.nadaParaVender')}</li>
          )}
          {naMochila.map(item => {
            return (
              <li key={item.id} title={itemHint(item)}>
                <Linha
                  lead={<ItemIcon id={item.defId} label={nomeDoItem(item.defId, item.name)} size={36} quantity={item.qty} eager />}
                  trail={(
                    <>
                      <span className="pack-st">{t('gm.item.moedas', { n: 0 })}</span>
                      {item.qty > 1 && (
                        <input
                          className="market-qty"
                          type="number"
                          min={1}
                          max={item.qty}
                          value={qty[item.id] ?? item.qty}
                          onChange={e => setQty(cur => ({ ...cur, [item.id]: Number(e.target.value) }))}
                          aria-label={t('gm.item.quantidadeDe', { item: nomeDoItem(item.defId, item.name) })}
                          disabled={busy}
                        />
                      )}
                      <button
                        type="button"
                        className={`btn btn--tiny${confirmId === item.id ? ' btn--danger' : ''}`}
                        disabled={busy}
                        onClick={() => void sell(item)}
                      >
                        {confirmId === item.id ? t('comum.confirmar') : t('gm.item.vender')}
                      </button>
                    </>
                  )}
                >
                  {nomeDoItem(item.defId, item.name)}
                </Linha>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function clamp(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, Math.trunc(n)));
}
